import { Injectable, inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { firstValueFrom } from 'rxjs';
// Import AdminAuthService from its source module, not the '@bodyanalytics/ui-core'
// barrel — this guard is itself re-exported by that barrel, so a barrel import
// would create a circular import-alias that breaks Jest compilation for consumers.
import { AdminAuthService } from '../services/auth/admin-auth.service';
import { safeErrorSummary } from '../helper/safe-error-summary';

/**
 * Roles permitted to enter the admin portal. The admin portal manages tenant data,
 * so only ADMIN and OPERATOR users may enter; CUSTOMER / USER must be turned away.
 *
 * NOTE (confirm before relying on this in prod): these strings must match the
 * `role` value the backend `tenant-api/admin-me` endpoint returns (stored by
 * AdminAuthService as `userRole`). Comparison is case-insensitive.
 */
export const ADMIN_PORTAL_ROLES = ['ADMIN', 'OPERATOR'];

/**
 * Route guard enforcing that the current user holds an admin-portal role (#1264).
 *
 * Before this guard, the admin app had NO role check: any authenticated Auth0 user
 * could reach admin data routes. The access model is DB-driven: the backend
 * resolves the caller's role from the users table via `tenant-api/admin-me`
 * (identity from the verified JWT), cached by {@link AdminAuthService}. The
 * former build-time ADMIN_EMAILS allowlist is gone — it locked newly
 * provisioned tenant owners out until a frontend rebuild/redeploy and added no
 * security (it shipped readable in a public bundle). This guard enforces the
 * model at the route level:
 *  - not authenticated  → redirect to `/login`
 *  - authenticated but no admin role → redirect to `/access-denied`
 *
 * If the role isn't cached yet (e.g. a deep link before the dashboard populated it),
 * the guard re-validates against the backend once, then re-reads the role. #2497:
 * the same applies when a role IS cached but {@link AdminAuthService.hasValidAdminTenantInfo}
 * says it's no longer within the 10-minute session-start-revalidated TTL — a
 * demoted admin's cached role is never trusted past that window, so the guard-gated
 * route-access exposure is genuinely bounded to ~10 minutes, not unbounded for the
 * life of the browser tab.
 */
@Injectable({ providedIn: 'root' })
export class AdminRoleGuard implements CanActivate, CanActivateChild {
  private authService = inject(AuthService);
  private adminAuthService = inject(AdminAuthService);
  private router = inject(Router);

  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    try {
      const isAuthenticated = await firstValueFrom(this.authService.isAuthenticated$);
      if (!isAuthenticated) {
        return this.router.createUrlTree(['/login']);
      }
      const access = await this.resolveAccess();
      if (access === 'allowed') {
        return true;
      }
      // #1553 Scope A / #2070: a genuine role denial ('denied') is a real access
      // decision, so the splash it redirects to shows no "Try again" escape
      // hatch (no `reason` query param). A verification FAILURE ('error' — the
      // identity lookup 5xx'd, the token fetch failed, or the catch below fired)
      // can dead-end a legitimate admin on a transient backend error, so it is
      // tagged `?reason=error` and the splash renders a retryable error state
      // instead of a silent denial.
      if (access === 'error') {
        return this.router.createUrlTree(['/access-denied'], { queryParams: { reason: 'error' } });
      }
      return this.router.createUrlTree(['/access-denied']);
    } catch (error) {
      // Fail closed: any error verifying access denies entry. #2133: never log
      // the raw error — an HttpErrorResponse embeds the request URL (and the
      // admin-me lookup's predecessor even carried the admin's email in it).
      console.error('AdminRoleGuard: error verifying admin access', safeErrorSummary(error));
      return this.router.createUrlTree(['/access-denied'], { queryParams: { reason: 'error' } });
    }
  }

  canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    return this.canActivate(childRoute, state);
  }

  /**
   * Resolves the access decision from the cached (or freshly-fetched) admin role.
   *
   *  - `'allowed'` — the role is in {@link ADMIN_PORTAL_ROLES}.
   *  - `'denied'`  — a real access decision (no admin record / role in the DB).
   *  - `'error'`   — the identity could not be VERIFIED (backend/token failure,
   *    #2070) — distinct from a denial so the caller can offer a retry.
   */
  private async resolveAccess(): Promise<'allowed' | 'denied' | 'error'> {
    let role = await this.adminAuthService.getUserRole();
    // #2497: a cached role is only trusted while it's within the session-start
    // TTL (AdminAuthService.hasValidAdminTenantInfo, 10 min). Skip the check
    // entirely when there's no role to begin with — nothing to trust either way.
    const isFresh = role ? await this.adminAuthService.hasValidAdminTenantInfo() : false;
    if (!role || !isFresh) {
      // Role missing, or cached but stale — re-validate against the backend
      // (DB role lookup via admin-me), then re-read. A caller with no admin
      // record (or a demotion) resolves 'not-admin' here, so the role stays
      // null/cleared and access is denied below; a lookup FAILURE resolves
      // 'error' and must surface as retryable, not as a denial.
      try {
        const status = await firstValueFrom(this.adminAuthService.resolveAdminIdentity());
        if (status === 'error') {
          return 'error';
        }
        role = await this.adminAuthService.getUserRole();
      } catch (error) {
        console.error('AdminRoleGuard: identity re-validation failed', safeErrorSummary(error));
        return 'error';
      }
    }
    return !!role && ADMIN_PORTAL_ROLES.includes(role.toUpperCase()) ? 'allowed' : 'denied';
  }
}
